import { useCallback, useLayoutEffect, useRef, type ReactNode } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useChrome, setSeedPanel, setRecPick } from './chromeStore'
import { ROOT_TABS, ROUTES } from './nav'
import { SeedChip, SeedPanel } from '../SeedChip'
import { useProfile, initialsOf } from '@/hooks/useProfile'
import { useAuthStore } from '@/store/auth'
import { useUnplacedCoins } from './useUnplacedCoins'

const Svg = ({ children }: { children: ReactNode }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true">{children}</svg>
)

/**
 * The phone's top bar (topbar.js v6, DEVELOPER-HANDOFF §4): one straight glass
 * bar, 96px, square ends, two rows.
 *   Row 1: the seeds as a number with the week's meter (and + when purchased
 *          seeds back it), and the account's initials on the right, which open
 *          Settings (account and preferences).
 *   Row 2: a root tab's large title and its actions, or "‹ Back" with the
 *          screen's name on a pushed screen.
 */
export function PhoneTopBar() {
  const title = useChrome((s) => s.title)
  const back = useChrome((s) => s.back)
  const lastRoot = useChrome((s) => s.lastRoot)
  const navKey = useChrome((s) => s.navKey)
  // Stable, so React never detaches and re-attaches the actions slot.
  const actRef = useCallback((el: HTMLDivElement | null) => { useChrome.getState().set({ phoneActions: el }) }, [])
  const navigate = useNavigate()
  const panel = useChrome((s) => s.seedPanel)
  const { data: profile } = useProfile()
  const email = useAuthStore((s) => s.user?.email)
  const coinDot = useUnplacedCoins()
  const rootTitle = navKey && ROOT_TABS[navKey] ? ROOT_TABS[navKey]!.title : title
  // Brian's topbar.js v6: the four screens under Feed controls show only
  // "‹ Feed controls"; other pushed screens centre their name. The name never
  // runs under the Back label or the actions: it gets the room between them,
  // truncates, and hides when there is too little.
  const showName = !!title && back?.to !== ROUTES.feedControls
  const rowRef = useRef<HTMLDivElement>(null)
  const backRef = useRef<HTMLButtonElement>(null)
  const nameRef = useRef<HTMLSpanElement>(null)
  useLayoutEffect(() => {
    const row = rowRef.current, b = backRef.current, n = nameRef.current
    if (!row || !b || !n) return
    const fit = () => {
      const r = row.getBoundingClientRect()
      const act = row.querySelector<HTMLElement>('.gg-topbar__act')
      const left = b.getBoundingClientRect().right - r.left
      const right = act && act.childElementCount ? r.right - act.getBoundingClientRect().left : 16
      const room = r.width - 2 * (Math.max(left, right) + 8)
      n.style.maxWidth = `${Math.max(0, room)}px`
      n.style.visibility = room < 56 ? 'hidden' : ''
    }
    fit()
    const ro = new ResizeObserver(fit)
    ro.observe(row); ro.observe(b)
    const act = row.querySelector('.gg-topbar__act')
    const mo = act ? new MutationObserver(fit) : null
    if (act && mo) mo.observe(act, { childList: true, subtree: true })
    return () => { ro.disconnect(); mo?.disconnect() }
  }, [showName, title, back?.label, lastRoot.label])

  function goBack() {
    if (back?.to) { navigate(back.to); return }
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0
    if (idx > 0) navigate(-1)
    else navigate(lastRoot.to)
  }

  return (
    <header className="gg-topbar">
      <div className="gg-topbar__row1">
        <SeedChip open={panel} onToggle={() => setSeedPanel(!panel)} />
        <NavLink to={ROUTES.settings} className="gg-initials" aria-label="Settings: account and preferences">
          <span>{initialsOf(profile?.full_name, email) || '·'}</span>
          {coinDot && <i className="gg-cdot" aria-hidden="true" />}
        </NavLink>
      </div>
      <div className="gg-topbar__row2" ref={rowRef}>
        {back ? (
          <>
            <button type="button" className="gg-back" onClick={goBack} ref={backRef}>
              <Svg><path d="M15 5l-7 7 7 7" /></Svg>{back.label || lastRoot.label || 'Back'}
            </button>
            {showName && <span className="gg-ptitle" ref={nameRef}>{title}</span>}
          </>
        ) : (
          <h2 className="gg-title">{rootTitle}</h2>
        )}
        <div className="gg-topbar__act" ref={actRef} />
      </div>
      {panel && <SeedPanel placement="phone" onClose={() => setSeedPanel(false)} />}
    </header>
  )
}

/**
 * The phone's tab bar, the top bar's size and glass: Feed, Library, Record ＋,
 * Alerts, Emma. Emma's tab holds Emma herself and opens her sheet.
 */
export function TabBar({ emma, alerts = 0, toRecord = 0 }: { emma: ReactNode; alerts?: number; toRecord?: number }) {
  const recOpen = useChrome((s) => s.recPick)
  const cls = ({ isActive }: { isActive: boolean }) => `gg-tab${isActive ? ' is-on' : ''}`
  return (
    <nav className="gg-tabbar" aria-label="GoGrov">
      <NavLink to={ROUTES.feed} end className={cls}>
        <Svg><path d="M4 11l8-7 8 7v9a1 1 0 0 1-1 1h-4v-6h-6v6H5a1 1 0 0 1-1-1z" /></Svg>Feed
      </NavLink>
      <NavLink to={ROUTES.library} className={cls}>
        <Svg><rect x="4" y="4" width="16" height="16" rx="2" /><path d="M4 10h16M10 10v10" /></Svg>Library
      </NavLink>
      {/* rec-pick.js: Record ＋ opens "Choose a story to record" over this screen, and carries a count while any are waiting. */}
      <button type="button" className={`gg-tab gg-tab--rec${recOpen ? ' is-on' : ''}`} onClick={() => setRecPick(true)}
        aria-haspopup="dialog" aria-expanded={recOpen}
        aria-label={toRecord ? `Record · ${toRecord} ${toRecord === 1 ? 'story' : 'stories'} to record` : 'Record'}>
        <span className="gg-tab__plus"><Svg><path d="M12 5v14M5 12h14" /></Svg></span>
        {toRecord > 0 && <span className="gg-tab__rc" aria-hidden="true">{toRecord > 9 ? '9+' : toRecord}</span>}
      </button>
      <NavLink to={ROUTES.alerts} className={cls}>
        <Svg><path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15z" /><path d="M10 20a2 2 0 0 0 4 0" /></Svg>Alerts
        {alerts > 0 && <span className="gg-tab__dot">{alerts > 9 ? '9+' : alerts}</span>}
      </NavLink>
      <div className="gg-tab gg-tab--emma">{emma}<span>Emma</span></div>
    </nav>
  )
}

